import * as React from "react";
import Map from "../map/Map";
import MapUtils from "../../utils/MapUtils";
import AppointmentDetails from "../../model/appointment/AppointmentDetails";
import {RootState} from "../../store/reducer/root/RootState";
import {connect} from "react-redux";
import {none, Option, option} from "ts-option";
import {RouteComponentProps, withRouter} from "react-router";
import User from "../../model/user/User";
import {getUserLocation} from "../../store/view/user";
import {Dispatch} from "redux";
import {fetchUserLocations} from "../../store/action/userLocation/userLocation";
import {OnFailure, OnSuccessData} from "../../store/action/actionHelpers";
import UserLocation from "../../model/userLocation/UserLocation";
import FormGroup from "../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import MapLegendComponent from "./MapLegendComponent";
import LatLng from "../../model/map/LatLng";
import Address from "../../model/address/Address";
import SelectPlace from "./place/SelectPlace";
import DateRangePickerComponent from "../ui/datepicker/DateRangePickerComponent";
import {RoutingMapFilters} from "../../store/local/model/RoutingMapFilters";
import LocalStorage from "../../store/local/LocalStorage";
import {isNullOrUndefined} from "util";
import MarkerWithInfoWindow from "../map/MarkerWithInfoWindow";
import DateUtils from "../../utils/DateUtils";
import Event from "../../model/event/Event";
import {notifyBugsnagAndShowToast} from "../../utils/Utils";
import {fetchJobsAppointmentsInDateRange, fetchLeadsAppointmentsInDateRange} from "../../store/action/appointment/appointment";
import moment = require("moment");
import deepEqual = require("deep-equal");
import Icon = google.maps.Icon;
import DirectionsRendererOptions = google.maps.DirectionsRendererOptions;
import DirectionsResult = google.maps.DirectionsResult;
import DirectionsStatus = google.maps.DirectionsStatus;

interface State {
	/**
	 * Appointments to show on a map (with locations).
	 */
	appointments: AppointmentDetails[],
	/**
	 * Filtered sales engineers that will be displayed on a map (with home locations).
	 */
	filteredSalesEngineers: User[],
	/**
	 * Sales engineers with home GPS locations.
	 */
	salesEngineers: User[],
	/**
	 * Start date of displayed appointments date range
	 */
	dateFrom: moment.Moment,
	/**
	 * End date of displayed appointments date range
	 */
	dateTo: moment.Moment,
	/**
	 * Lattitude and langitude passed to map.
	 */
	center: {
		lat: number,
		lng: number
	}
	/**
	 * Direction result
	 */
	directions: DirectionsResult;
	/**
	 * Direction origin
	 */
	fromId: Option<string>;
	/**
	 * Direction destination
	 */
	toId: Option<string>;
	/**
	 * Sales engineers selected on map legend.
	 */
	mapLegendSelectedEngineers: User[],
	/**
	 * Selected route index.
	 */
	routeIndex: number,
	/**
	 * Routing map stored filters object.
	 */
	storedData: RoutingMapFilters;
}

export interface Place {
	/**
	 * Place ID. Possible values: appointment id for appointment location / user.id for user home location / user.id + "1" for user current location
	 */
	id: string,
	/**
	 * Display text
	 */
	text: string,
	/**
	 * Appointment detail
	 */
	appointmentDetail: Option<AppointmentDetails>,
	/**
	 * Latitude and longitude
	 */
	latLng: Option<LatLng>,
	/**
	 * Address
	 */
	address: Option<Address>,
	/**
	 * Info window content
	 */
	infoText: Option<string>,
	/**
	 * Event to display on tooltip.
	 */
	event: Option<Event>,
}

interface ReduxProps {
	/**
	 * Sales engineers (without home location).
	 */
	salesEngineers: User[]
}

interface DispatchProps {
	fetchUserLocations: (roles: string[], onSuccess: OnSuccessData<UserLocation[]>, onFailure: OnFailure) => void;
	fetchLeadsAppointmentsInDateRange: (dateFrom: moment.Moment,
		dateTo: moment.Moment,
		onSuccess: OnSuccessData<AppointmentDetails[]>) => void
	fetchJobsAppointmentsInDateRange: (dateFrom: moment.Moment,
		dateTo: moment.Moment,
		onSuccess: OnSuccessData<AppointmentDetails[]>) => void
}

type Props = ReduxProps & DispatchProps & RouteComponentProps<{}>

/**
 * Schedule map component
 */
class ScheduleMap extends React.PureComponent<Props, State> {

	/**
	 * Pulling timer identifier.
	 * @type {number}
	 */
	private pullingTimerId: number = null;
	/**
	 * Direction service
	 */
	private directionsService: google.maps.DirectionsService;
	/**
	 * Direction renderer
	 */
	private directionsRenderer: google.maps.DirectionsRenderer;

	constructor(props: Props) {
		super(props);
		const filters = LocalStorage.getRoutingMapFilters();
		this.state = {
			appointments: [],
			filteredSalesEngineers: this.props.salesEngineers,
			salesEngineers: [],
			dateFrom: moment(filters.dateFrom),
			dateTo: moment(filters.dateTo),
			center: {
				lat: 51.919516,
				lng: -0.2850921
			},
			directions: null,
			fromId: option(filters.directionFromId),
			toId: option(filters.directionToId),
			mapLegendSelectedEngineers: filters.undisplayedSalesEngineersIds ?
				this.props.salesEngineers.filter(
					se => filters.undisplayedSalesEngineersIds.every(seId => seId !== se.id)) :
				this.props.salesEngineers,
			routeIndex: filters.routeIndex,
			storedData: filters
		};
		this.onSalesEngineerChange = this.onSalesEngineerChange.bind(this);
		this.fetchCurrentPositions = this.fetchCurrentPositions.bind(this);
		this.onFocusButtonClick = this.onFocusButtonClick.bind(this);
		this.createDirection = this.createDirection.bind(this);
		this.getPlaces = this.getPlaces.bind(this);
		this.onRouteIndexChange = this.onRouteIndexChange.bind(this);
	}

	componentDidMount() {
		this.fetchAppointments();
		this.populateEngineersLocations(this.props.salesEngineers);
		this.pullingTimerId = window.setInterval(this.fetchCurrentPositions, 15 * 1000);
		this.fetchCurrentPositions();
	}

	fetchAppointments = () => {
		const dateFrom = this.state.dateFrom;
		const dateTo = this.state.dateTo;
		this.setState({appointments: []}, () => {
			this.props.fetchLeadsAppointmentsInDateRange(dateFrom, dateTo, this.populateAppointmentsLocations)
			this.props.fetchJobsAppointmentsInDateRange(dateFrom, dateTo, this.populateAppointmentsLocations)
		})
	}

	createDirection(places: Place[]) {
		if (!this.state.directions && this.state.fromId.isDefined && this.state.toId.isDefined) {
			const [placeFrom] = places.filter(p => p.id === this.state.fromId.get);
			const [placeTo] = places.filter(p => p.id === this.state.toId.get);
			if (!isNullOrUndefined(placeFrom) && !isNullOrUndefined(placeTo)) {
				this.directionsService.route({
					origin: this.placeToString(placeFrom),
					destination: this.placeToString(placeTo),
					travelMode: google.maps.TravelMode.DRIVING,
					provideRouteAlternatives: true

				}, (result: DirectionsResult, status: DirectionsStatus) => {
					if (status === google.maps.DirectionsStatus.OK) {
						this.setState({
							directions: result
						});
					}
				});
			}
		}
	}

	componentWillUnmount() {
		window.clearInterval(this.pullingTimerId);
	}

	componentWillReceiveProps(nextProps: ReduxProps) {
		if (this.props.salesEngineers.length != nextProps.salesEngineers.length) {
			this.setState({
				filteredSalesEngineers: nextProps.salesEngineers,
				mapLegendSelectedEngineers: this.state.storedData.undisplayedSalesEngineersIds ?
					nextProps.salesEngineers.filter(
						se => this.state.storedData.undisplayedSalesEngineersIds.every(seId => seId !== se.id)) :
					nextProps.salesEngineers
			});
			this.populateEngineersLocations(nextProps.salesEngineers);
		}
	}

	render() {
		const places = this.getPlaces(this.state.appointments);

		return (
			<div className={"c-schedule-map"}>
				<div className={"c-schedule-map_map"}>
					<Map center={this.state.center} zoom={9} onInit={(gm) => {
						this.directionsService = new google.maps.DirectionsService();
						this.directionsRenderer =
							this.directionsRenderer == null ? new google.maps.DirectionsRenderer() : this.directionsRenderer;
						if (gm != null && this.state.directions &&
							!deepEqual(this.directionsRenderer.getDirections(), this.state.directions)) {
							const mapRef = gm.context[MapUtils.MAP_REF_CONTEXT_KEY];
							const dirRendererOptions: DirectionsRendererOptions = {
								directions: this.state.directions,
								routeIndex: this.state.routeIndex,
								panel: document.getElementById('directionPanel'),
								map: mapRef,
								suppressMarkers: true
							};
							this.directionsRenderer.setOptions(dirRendererOptions);
							google.maps.event.addListener(this.directionsRenderer, 'routeindex_changed',
								() => this.onRouteIndexChange(this.directionsRenderer.getRouteIndex()));
						}
						!this.state.directions && this.directionsRenderer.setDirections({routes: [], geocoded_waypoints: []});
						this.createDirection(places);
					}}>
						{this.state.appointments.map((details, index) => {
							const event = details.toEvent();
							return (
								<MarkerWithInfoWindow
									opacity={moment(details.appointment.dateTime).add(2, "h").isBefore(moment()) ? 0.5 : 1}
									key={index}
									position={details.location.get}
									zIndex={3}
									infoText={none}
									icon={{
										url: MapUtils.getEventMarker(event)
									}}
									event={option(event)}/>
							)
						})}
						{this.state.filteredSalesEngineers.map((engineer, index) => {
							return engineer.data.homeAddressLocation.map(location => {
								const seName = engineer.getDisplayName(true);
								const seAddress = engineer.data.homeAddress.map(a => a.displayName()).getOrElse("");
								return (
									<MarkerWithInfoWindow key={index}
														  position={location}
														  icon={{
															  url: MapUtils.getHomeMarker(engineer.id),
															  scaledSize: {width: 48, height: 48}
														  } as Icon}
														  infoText={option(`${seName}\n\n${seAddress}`)}
														  event={none}
														  zIndex={1}/>
								)
							}).orNull;
						})}
						{this.state.filteredSalesEngineers.map((engineer, index) => {
							const userLocation = getUserLocation(engineer.id);
							return (
								userLocation.map(ul => {
										return (
											<MarkerWithInfoWindow
												key={index}
												position={{lat: ul.latitude, lng: ul.longitude}}
												icon={{
													url: MapUtils.getCurrentPositionMarker(engineer, ul),
													scaledSize: {width: 48, height: 48}
												} as Icon}
												event={none}
												infoText={none}
												zIndex={2}/>
										)
									}
								).orNull
							)
						})}

						{this.state.directions &&
						[this.state.fromId.map(f => {
							const [from] = places.filter(p => p.id == f);
							return !isNullOrUndefined(from) ? (
									<MarkerWithInfoWindow key={1}
														  position={this.state.directions.routes[0].legs[0].start_location}
														  icon={{url: MapUtils.MARKER_A}}
														  infoText={from.infoText}
														  event={from.event}/>) :
								this.directionsRenderer.setDirections({routes: [], geocoded_waypoints: []});
						}).orNull,
							this.state.toId.map(t => {
								const [to] = places.filter(p => p.id == t);
								return !isNullOrUndefined(to) ? (
										<MarkerWithInfoWindow key={2} position={this.state.directions.routes[0].legs[0].end_location}
															  icon={{url: MapUtils.MARKER_B}}
															  infoText={to.infoText}
															  event={to.event}/>) :
									this.directionsRenderer.setDirections({routes: [], geocoded_waypoints: []});
							}).orNull]}
					</Map>
				</div>
				<div className={"c-schedule-map_rightPanel"}>
					<div className={"t-hero"}>Appointments</div>
					<div className={"c-schedule-map_rightPanel_info"}>
						Please select date range of appointments visible on a map or routing
					</div>
					<FormGroup>
						<DateRangePickerComponent from={this.state.dateFrom}
												  to={this.state.dateTo}
												  onChange={this.onDateChange}
												  displayBlock
												  range={2}/>
					</FormGroup>
					<div className={"t-hero"}>Users locations</div>
					<div className="users-locations">
						<MapLegendComponent checkedSalesEngineers={this.state.mapLegendSelectedEngineers}
											salesEngineers={this.props.salesEngineers}
											onChange={this.onSalesEngineerChange}
											onFocusButtonClick={this.onFocusButtonClick}/>
					</div>
					<div className={"t-hero"}>Directions</div>
					<SelectPlace
						clearable={true}
						placeholder={"From"}
						items={this.state.toId.map(toId => places.filter(p => p.id != toId)).orElse(
							() => option(places))}
						onChanged={(p: Option<Place>) => this.setState(
							{
								fromId: p.map(p => p.id),
								directions: null,
								storedData: this.state.storedData.updateDirectionFrom(p.map(p => p.id).orNull)
							}, () => {
								if (this.state.directions) document.getElementById('directionPanel').innerHTML = ''
							})}
						selectedId={this.state.fromId}/>
					<SelectPlace
						clearable={true}
						placeholder={"To"}
						items={this.state.fromId.map(fromId => places.filter(p => p.id != fromId)).orElse(
							() => option(places))}
						onChanged={(p: Option<Place>) => this.setState(
							{
								toId: p.map(p => p.id),
								directions: null,
								storedData: this.state.storedData.updateDirectionTo(p.map(p => p.id).orNull)
							}, () => {
								if (this.state.directions) document.getElementById('directionPanel').innerHTML = ''
							})}
						selectedId={this.state.toId}/>
					{this.state.directions &&
					<div>
						<div className={"t-hero"}>Routes</div>
						<div id={"directionPanel"}/>
					</div>}
				</div>

			</div>
		);
	}

	/**
	 * Selected route index change callback.
	 * @param {number} routeIndex
	 */
	onRouteIndexChange(routeIndex: number) {
		this.setState({routeIndex: routeIndex, storedData: this.state.storedData.updateRouteIndex(routeIndex)});
	}

	fetchCurrentPositions() {
		this.props.fetchUserLocations(["SALES_ENGINEER"], () => {}, () => {});
	}

	/**
	 * On sales engineers list changed.
	 * @param {Option<User[]>} selectedEngineers
	 */
	onSalesEngineerChange(selectedEngineers: User[]) {
		const selectedEngineersIds = selectedEngineers.map(se => se.id);
		const engineersWithLocations = this.state.salesEngineers.filter(se => selectedEngineersIds.indexOf(se.id) > -1);
		const engineersWithLocationsIds = engineersWithLocations.map(se => se.id);
		const engineersWithoutLocations = selectedEngineers
			.filter(se => engineersWithLocationsIds.indexOf(se.id) == -1);
		this.setState({
			filteredSalesEngineers: engineersWithLocations.concat(engineersWithoutLocations),
			mapLegendSelectedEngineers: selectedEngineers,
			storedData: this.state.storedData.updateUndisplayedSalesEngineers(
				this.props.salesEngineers.map(e => e.id).filter(seId => selectedEngineers.every(se => se.id !== seId)))
		});
	}

	/**
	 * Populate appointments details with locations.
	 * @param {AppointmentDetails[]} appointments
	 */
	private populateAppointmentsLocations = (appointments: AppointmentDetails[]) => {
		const promises = appointments.map(MapUtils.populateAppointmentLocation);
		Promise.all(promises).then(results => {
			const found: AppointmentDetails[] = [];
			//use this to show invalid addresses
			const notFound: AppointmentDetails[] = [];
			results.map(details => {
				if (details.location.isDefined) {
					found.push(details)
				}
				else {
					notFound.push(details);
				}
			});
			this.setState({appointments: this.state.appointments.concat(found)});
		})
	}

	/**
	 * Handles click on focus button on map legend
	 * @param {User} user
	 */
	onFocusButtonClick(user: User) {
		getUserLocation(user.id).map(l => this.setState({center: {lat: l.latitude, lng: l.longitude}}));
	}

	/**
	 * Populate sales engineers home address GPS locations.
	 * @param {User[]} users
	 */
	private populateEngineersLocations(users: User[]) {
		const promises = users.map(MapUtils.populateUserLocation);
		Promise.all(promises).then(results => {
			const found: User[] = [];
			results.forEach(user => {
				if (user.data.homeAddressLocation.isDefined || getUserLocation(user.id).isDefined) {
					found.push(user)
				}
			});
			this.setState({salesEngineers: found, filteredSalesEngineers: found})
		})
	}

	getPlaces(appointments: AppointmentDetails[]): Place[] {
		const places: Place[] =
			appointments.map(a => {
				const referenceNumber = a.getReferenceNumber();
				const date = a.appointment.dateTime.format(
					DateUtils.DATE_TIME_FORMAT);
				const contactName = a.individualContact.data.displayName();
				const seNames = a.getSalesEngineers().map(users => users.join(", ")).getOrElse("")
				return {
					id: a.appointment.id,
					text: `${referenceNumber} ${date} ${contactName}${a.site.map(s => ` ${s.data.address.postcode}`).getOrElse(
						"")} ${seNames}`,
					appointmentDetail: option(a),
					latLng: none,
					address: a.site.map(s => s.data.address),
					infoText: none,
					event: option(a.toEvent())
				}
			});

		this.state.filteredSalesEngineers.forEach(e => {
			e.data.homeAddress.map(
				ha => {
					const seName = e.getDisplayName(true);
					const seAddress = e.data.homeAddress.map(a => a.displayName()).getOrElse("");
					places.push(
						{
							id: e.id,
							text: e.getDisplayName() + " home location",
							appointmentDetail: none,
							latLng: none,
							address: option(ha),
							infoText: option(`${seName}\n\n${seAddress}`),
							event: none
						})
				});
			getUserLocation(e.id).map(ul => {
					if (!ul.isOffline()) {
						places.push({
							// Add "1" to differentiate user home location and user current location
							id: e.id + "1",
							text: e.getDisplayName() + " current location",
							appointmentDetail: none,
							latLng: option(new LatLng(ul.latitude, ul.longitude)),
							address: none,
							infoText: none,
							event: none
						});
					}
				}
			)
		});
		return places;
	}

	/**
	 * Return comma separated latitude and longitude if defined or geocoding address
	 * @returns {string}
	 */
	private placeToString(place: Place) {
		return place.latLng.isDefined ? place.latLng.map(latLng => `${latLng.lat}, ${latLng.lng}`).get :
			place.address.map(a => a.geocodingAddress()).get;
	};

	onDateChange = (dateFrom: moment.Moment, dateTo: moment.Moment) => this.setState(
		{dateFrom, dateTo, storedData: this.state.storedData.updateDateFrom(dateFrom).updateDateTo(dateTo)},
		this.fetchAppointments)

}

function mapStateToProps(state: RootState) {
	const engineers = state.user.users.map(users => users.filter(u => u.isSalesEngineer())).getOrElse([]);
	return {
		salesEngineers: engineers,
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchUserLocations: (roles: string[], onSuccess: OnSuccessData<UserLocation[]>, onFailure: OnFailure) => dispatch(
			fetchUserLocations(roles, onSuccess, onFailure)),
		fetchLeadsAppointmentsInDateRange: (dateFrom: moment.Moment,
			dateTo: moment.Moment,
			onSuccess: OnSuccessData<AppointmentDetails[]>) => dispatch(
			fetchLeadsAppointmentsInDateRange(dateFrom, dateTo, onSuccess,
				e => notifyBugsnagAndShowToast(e, "Fetch Leads appointments"))),
		fetchJobsAppointmentsInDateRange: (dateFrom: moment.Moment,
			dateTo: moment.Moment,
			onSuccess: OnSuccessData<AppointmentDetails[]>) => dispatch(
			fetchJobsAppointmentsInDateRange(dateFrom, dateTo, onSuccess,
				e => notifyBugsnagAndShowToast(e, "Fetch Jobs appointments")))
	}
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ScheduleMap));